// typescript
//
// Pure, client-side "did you mean …?" detector for likely email typos.
//
// Design: designs/04-live/2026-06-10-email-typo-nudge-ux-design.md
//
// Guiding principle: be INVISIBLE WHEN WRONG. A false suggestion on an
// already-correct address is worse than silence, so this module is tuned for
// high precision over recall: it suggests AT MOST ONE correction, only when
// confident, never alters the local part, and never touches a domain it
// recognizes as real. Everything here is synchronous and dependency-free so it
// is trivially unit-testable (see emailTypo.spec.ts).

import {EMAIL_PATTERN} from "@/main/validation/request";

export type EmailCorrection = {
    /** The corrected, ready-to-use email address. Always passes EMAIL_PATTERN. */
    suggestion: string;
};

// Domains we actively suggest TOWARD — the highest-volume consumer and
// locale-relevant providers. A typed domain that is a near-miss of one of these
// gets corrected to it. Keep this list curated so neighbors are not themselves
// real domains.
const SUGGESTION_TARGET_DOMAINS: readonly string[] = [
    "gmail.com",
    "googlemail.com",
    "yahoo.com",
    "yahoo.co.uk",
    "outlook.com",
    "hotmail.com",
    "hotmail.co.uk",
    "live.com",
    "icloud.com",
    "me.com",
    "aol.com",
    "proton.me",
    "protonmail.com",
    "msn.com",
    // German
    "gmx.de",
    "gmx.net",
    "web.de",
    "t-online.de",
    // Russian
    "mail.ru",
    "yandex.ru",
    // Chinese / Korean
    "qq.com",
    "163.com",
    "naver.com",
    // Other
    "zoho.com",
];

// Real domains we must NEVER "correct", even when they sit one edit away from a
// suggestion target. This is the precision guard: e.g. "email.com" and
// "ymail.com" are both one edit from "gmail.com" but are legitimate providers.
// Superset of the targets plus a curated list of real-but-colliding domains.
const ACCEPTED_DOMAINS: ReadonlySet<string> = new Set<string>([
    ...SUGGESTION_TARGET_DOMAINS,
    // Distance-1 neighbors of targets that are real providers — guard hardest.
    "email.com",
    "ymail.com",
    "mail.com",
    "live.co.uk",
    "live.de",
    "live.fr",
    // Other widely-used real mailboxes we should leave untouched.
    "fastmail.com",
    "hey.com",
    "pm.me",
    "mac.com",
    "comcast.net",
    "verizon.net",
    "att.net",
    "sbcglobal.net",
    "cox.net",
    "btinternet.com",
    "ntlworld.com",
    // Locale providers (low collision risk, still explicitly accepted).
    "yahoo.de", "yahoo.fr", "yahoo.es", "yahoo.it", "yahoo.ca",
    "outlook.de", "outlook.fr", "outlook.es", "outlook.it",
    "hotmail.fr", "hotmail.es", "hotmail.de", "hotmail.it",
    "gmx.at", "gmx.ch", "freenet.de", "arcor.de",
    "libero.it", "virgilio.it", "alice.it", "tiscali.it",
    "orange.fr", "wanadoo.fr", "free.fr", "laposte.net", "sfr.fr", "bbox.fr",
    "terra.com", "uol.com.br", "bol.com.br", "globo.com",
    "seznam.cz", "centrum.cz",
    "wp.pl", "o2.pl", "interia.pl", "onet.pl", "op.pl",
    "telenet.be", "skynet.be",
    "ziggo.nl", "kpnmail.nl", "home.nl", "hotmail.nl", "live.nl",
    "bluewin.ch",
    "yandex.com", "rambler.ru", "bk.ru", "inbox.ru", "list.ru",
]);

// Common TLDs we correct TOWARD, in priority order. The first match wins, so a
// typo like "con" resolves to "com" rather than the also-one-edit-away "co".
const COMMON_TLDS: readonly string[] = [
    "com", "net", "org", "edu", "gov", "io", "co", "info", "biz",
    "us", "uk", "ca", "de", "fr", "es", "it", "nl", "ru", "br",
];

// Valid TLDs we must never "correct" — a typed value ending in one of these has
// a legitimate TLD, so we leave its TLD alone (a near-miss domain rule may still
// fire). Broad enough to cover ccTLDs that collide with common ones (e.g. "cm",
// "co", "om" all sit one edit from "com").
const KNOWN_TLDS: ReadonlySet<string> = new Set<string>([
    // popular gTLDs
    "com", "net", "org", "edu", "gov", "mil", "int", "info", "biz", "name",
    "io", "co", "me", "tv", "app", "dev", "xyz", "online", "site", "shop",
    "blog", "tech", "store", "cloud", "ai", "eu", "pro", "club",
    // ccTLDs (broad, includes single-edit neighbors of common gTLDs)
    "ac", "ad", "ae", "af", "ag", "ai", "al", "am", "ao", "ar", "at", "au",
    "az", "ba", "bb", "bd", "be", "bg", "bh", "bm", "bn", "bo", "br", "bs",
    "bw", "by", "bz", "ca", "cd", "cf", "cg", "ch", "ci", "cl", "cm", "cn",
    "cr", "cu", "cv", "cx", "cy", "cz", "de", "dk", "dm", "do", "dz", "ec",
    "ee", "eg", "es", "et", "fi", "fj", "fo", "fr", "ga", "ge", "gg", "gh",
    "gi", "gl", "gm", "gr", "gt", "gy", "hk", "hn", "hr", "ht", "hu", "id",
    "ie", "il", "im", "in", "iq", "ir", "is", "it", "je", "jm", "jo", "jp",
    "ke", "kg", "kh", "kr", "kw", "ky", "kz", "la", "lb", "lc", "li", "lk",
    "lt", "lu", "lv", "ly", "ma", "mc", "md", "me", "mg", "mk", "ml", "mn",
    "mo", "mq", "mr", "mt", "mu", "mv", "mw", "mx", "my", "mz", "na", "ne",
    "ng", "ni", "nl", "no", "np", "nz", "om", "pa", "pe", "pf", "pg", "ph",
    "pk", "pl", "pr", "ps", "pt", "py", "qa", "ro", "rs", "ru", "rw", "sa",
    "sc", "sd", "se", "sg", "si", "sk", "sl", "sm", "sn", "so", "sr", "sv",
    "sy", "sz", "tc", "td", "tg", "th", "tj", "tm", "tn", "to", "tr", "tt",
    "tw", "tz", "ua", "ug", "uk", "us", "uy", "uz", "va", "vc", "ve", "vg",
    "vn", "vu", "ws", "ye", "za", "zm", "zw",
]);

/**
 * True iff `a` and `b` differ by exactly one edit: a single insertion, deletion,
 * substitution, or adjacent transposition (Damerau). Transposition matters here
 * because real typos like "cmo"->"com" and "gmial"->"gmail" are swaps, not
 * substitutions. Returns false when equal. Bounded and allocation-free.
 */
function isOneEdit(a: string, b: string): boolean {
    if (a === b) {
        return false;
    }
    const la = a.length;
    const lb = b.length;
    if (Math.abs(la - lb) > 1) {
        return false;
    }
    if (la === lb) {
        const diffs: number[] = [];
        for (let i = 0; i < la; i++) {
            if (a[i] !== b[i]) {
                diffs.push(i);
                if (diffs.length > 2) {
                    return false;
                }
            }
        }
        if (diffs.length === 1) {
            return true;
        }
        // Two differences only count as one edit when they are an adjacent swap.
        if (diffs.length === 2) {
            const [p, q] = diffs;
            return q === p + 1 && a[p] === b[q] && a[q] === b[p];
        }
        return false;
    }
    // Lengths differ by one: check for a single insertion/deletion.
    const longer = la > lb ? a : b;
    const shorter = la > lb ? b : a;
    let i = 0;
    let j = 0;
    let skipped = false;
    while (i < longer.length && j < shorter.length) {
        if (longer[i] === shorter[j]) {
            i++;
            j++;
        } else {
            if (skipped) {
                return false;
            }
            skipped = true;
            i++;
        }
    }
    return true;
}

/** Unique suggestion target exactly one edit away from `domain`, else null. */
function nearMissDomain(domain: string): string | null {
    let match: string | null = null;
    for (const target of SUGGESTION_TARGET_DOMAINS) {
        if (isOneEdit(domain, target)) {
            if (match && match !== target) {
                // Ambiguous — two plausible corrections. Stay silent.
                return null;
            }
            match = target;
        }
    }
    return match;
}

/** Corrected domain when only the TLD is a near-miss of a common TLD, else null. */
function nearMissTld(domain: string): string | null {
    const lastDot = domain.lastIndexOf(".");
    if (lastDot <= 0) {
        return null;
    }
    const tld = domain.slice(lastDot + 1);
    const rest = domain.slice(0, lastDot);
    // Only correct a clearly-wrong TLD: at least two chars, and not itself valid.
    if (tld.length < 2 || KNOWN_TLDS.has(tld)) {
        return null;
    }
    for (const candidate of COMMON_TLDS) {
        if (isOneEdit(tld, candidate)) {
            return `${rest}.${candidate}`;
        }
    }
    return null;
}

/** Target domain whose dot was dropped (e.g. "gmailcom" -> "gmail.com"), else null. */
function missingDotDomain(domain: string): string | null {
    if (domain.includes(".")) {
        return null;
    }
    for (const target of SUGGESTION_TARGET_DOMAINS) {
        if (domain === target.replace(/\./g, "")) {
            return target;
        }
    }
    return null;
}

/**
 * Suggest a single high-confidence correction for a likely-mistyped email, or
 * null when we are not confident. The returned suggestion always passes
 * EMAIL_PATTERN and never equals the input. The local part is never modified.
 */
export function suggestEmailCorrection(rawValue: string | null | undefined): EmailCorrection | null {
    if (!rawValue) {
        return null;
    }
    const value = rawValue.trim();
    if (!value || /\s/.test(value)) {
        return null;
    }

    const at = value.indexOf("@");
    // Require exactly one "@", with a non-empty local part and domain.
    if (at <= 0 || at !== value.lastIndexOf("@") || at === value.length - 1) {
        return null;
    }

    const local = value.slice(0, at);
    const domain = value.slice(at + 1).toLowerCase();

    // Already a domain we recognize as real — never second-guess it.
    if (ACCEPTED_DOMAINS.has(domain)) {
        return null;
    }

    const correctedDomain =
        nearMissDomain(domain) ??
        nearMissTld(domain) ??
        missingDotDomain(domain);

    if (!correctedDomain || correctedDomain === domain) {
        return null;
    }

    const suggestion = `${local}@${correctedDomain}`;
    if (suggestion === value || !EMAIL_PATTERN.test(suggestion)) {
        return null;
    }

    return {suggestion};
}
